import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { getAri } from "../api";
import {
  transformAriResponse,
  getPrioritizedRoomBatches,
  mergeAriDataByRoomtype,
  getSanitizeDataForMissingAris,
} from "../constants/ARI-utils";
import { ARI_BATCH_SIZE, ARI_BATCH_THRESHOLD } from "../constants";

export const AriContext = React.createContext({});

export const AriProvider = React.memo(function AriProvider({ children }) {
  const [ariData, setAriData] = useState([]);
  // Foreground fetch: the priority batch of a full fetch (or the whole fetch
  // for <=ARI_BATCH_THRESHOLD rooms) and the first chunk of a scoped refresh.
  const [loading, setLoading] = useState(false);
  // True while queued background batches remain for the current generation.
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [loadedRoomtypeIds, setLoadedRoomtypeIds] = useState(new Set());

  // Batches waiting to be fetched:
  // [{ rooms, gen, isForeground, isRefresh, params }].
  // Each batch carries the params of the call that enqueued it, so a scoped
  // refresh fetches the caller's window even if it differs from the full
  // fetch's (and cannot dereference null before any full fetch has run).
  const queueRef = useRef([]);
  const drainingRef = useRef(false);
  // Bumped on every full fetch; in-flight results from an older generation
  // are discarded, which makes property/rateplan/date switches mid-sequence
  // race-free without request cancellation.
  const fetchGenRef = useRef(0);

  const handleBatchFailure = useCallback((batch, batchRoomIds) => {
    if (batch.isForeground && !batch.isRefresh) {
      // Legacy parity: a failed foreground batch of a FULL fetch clears the
      // data and abandons the rest of this generation's queue.
      setAriData([]);
      queueRef.current = queueRef.current.filter(
        ({ gen }) => gen !== batch.gen
      );
    } else {
      // A failed background batch must not block tab switches, and a failed
      // scoped-refresh chunk must not wipe the cache the user is looking at —
      // mark the rooms loaded (refresh rooms keep their previous values,
      // never-loaded rooms render as empty) and carry on with the queue.
      setLoadedRoomtypeIds((prev) => new Set([...prev, ...batchRoomIds]));
    }
  }, []);

  // Fetch one batch and merge its records into the cache. Results (and
  // failures) landing after a newer full fetch bumped the generation are
  // discarded.
  const runBatch = useCallback(
    async (batch) => {
      const { propertyId, rateplanId, startDate, endDate, includeRoomsSold } =
        batch.params;
      const batchRoomIds = batch.rooms.map(({ id }) => id);

      try {
        const response = await getAri({
          propertyId,
          rateplanIds: [rateplanId],
          roomtypeIds: batchRoomIds,
          startDate,
          endDate,
          includeNumberOfRoomsSold: includeRoomsSold,
        });
        if (batch.gen !== fetchGenRef.current) {
          return;
        }
        const records = getSanitizeDataForMissingAris(
          transformAriResponse(response?.data?.aris),
          startDate,
          endDate,
          batch.rooms
        );
        setAriData((prev) =>
          mergeAriDataByRoomtype(prev, records, batchRoomIds)
        );
        setLoadedRoomtypeIds((prev) => new Set([...prev, ...batchRoomIds]));
      } catch {
        if (batch.gen !== fetchGenRef.current) {
          return;
        }
        handleBatchFailure(batch, batchRoomIds);
      } finally {
        if (batch.gen === fetchGenRef.current && batch.isForeground) {
          setLoading(false);
        }
      }
    },
    [handleBatchFailure]
  );

  const processQueue = useCallback(async () => {
    if (drainingRef.current) {
      return;
    }
    drainingRef.current = true;

    while (queueRef.current.length > 0) {
      const batch = queueRef.current.shift();
      if (batch.gen === fetchGenRef.current) {
        await runBatch(batch);
      }
    }

    drainingRef.current = false;
    setBackgroundLoading(false);
  }, [runBatch]);

  const refetchAriData = useCallback(
    ({
      propertyId,
      rateplanId,
      rooms,
      startDate,
      endDate,
      includeNumberOfRoomsSold = false,
      priorityRoomtypeId,
      roomtypeIdsToRefresh,
    }) => {
      if (
        !propertyId ||
        !rateplanId ||
        !rooms?.length ||
        !startDate ||
        !endDate
      ) {
        return;
      }

      const params = {
        propertyId,
        rateplanId,
        startDate,
        endDate,
        includeRoomsSold: includeNumberOfRoomsSold,
      };

      if (roomtypeIdsToRefresh?.length) {
        // Scoped post-update refresh: same generation, merge into cache.
        const refreshIds = new Set(roomtypeIdsToRefresh);
        const affectedRooms = rooms.filter(({ id }) => refreshIds.has(id));
        if (!affectedRooms.length) {
          return;
        }
        const chunks = [];
        for (let i = 0; i < affectedRooms.length; i += ARI_BATCH_SIZE) {
          chunks.push(affectedRooms.slice(i, i + ARI_BATCH_SIZE));
        }
        // Refresh chunks run before any still-pending initial batches so the
        // just-updated rooms show fresh values first.
        queueRef.current = [
          ...chunks.map((chunkRooms, index) => ({
            rooms: chunkRooms,
            gen: fetchGenRef.current,
            isForeground: index === 0,
            isRefresh: true,
            params,
          })),
          ...queueRef.current,
        ];
        setLoading(true);
        if (chunks.length > 1) {
          setBackgroundLoading(true);
        }
        processQueue();
        return;
      }

      // Full fetch: new generation, cache reset, prioritized batches.
      const gen = ++fetchGenRef.current;
      setAriData([]);
      setLoadedRoomtypeIds(new Set());

      const batches = getPrioritizedRoomBatches(
        rooms,
        priorityRoomtypeId ?? rooms[0]?.id,
        ARI_BATCH_SIZE,
        ARI_BATCH_THRESHOLD
      );
      queueRef.current = batches.map((batchRooms, index) => ({
        rooms: batchRooms,
        gen,
        isForeground: index === 0,
        params,
      }));
      setLoading(true);
      setBackgroundLoading(batches.length > 1);
      processQueue();
    },
    [processQueue]
  );

  // Move a not-yet-fetched room's batch to the front of the queue so the tab
  // the user just opened loads next.
  const prioritizeRoomtype = useCallback((roomtypeId) => {
    const index = queueRef.current.findIndex(({ rooms }) =>
      rooms.some(({ id }) => id === roomtypeId)
    );
    if (index > 0) {
      const [batch] = queueRef.current.splice(index, 1);
      queueRef.current.unshift(batch);
    }
  }, []);

  // Memoize the value object to avoid re-creating it on every render
  const contextValue = useMemo(
    () => ({
      ariData,
      refetchAriData,
      loading,
      backgroundLoading,
      loadedRoomtypeIds,
      prioritizeRoomtype,
    }),
    [
      ariData,
      refetchAriData,
      loading,
      backgroundLoading,
      loadedRoomtypeIds,
      prioritizeRoomtype,
    ]
  );

  return (
    <AriContext.Provider value={contextValue}>{children}</AriContext.Provider>
  );
});

AriProvider.propTypes = {
  children: PropTypes.node.isRequired, // Validate that `children` is passed and is a React node
};

export const useAriContext = () => useContext(AriContext);
